"use client";

import {
	sdkActionManager,
	useEmbedNonStylesConfig,
	useEmbedStyles,
	useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import { LOGO } from "@calcom/lib/constants";
import { useRouterQuery } from "@calcom/lib/hooks/useRouterQuery";
import useTheme from "@calcom/lib/hooks/useTheme";
import { UserAvatar } from "@calcom/ui/components/avatar";
import { Icon } from "@calcom/ui/components/icon";
import { OrgBanner } from "@calcom/ui/components/organization-banner";
import { UnpublishedEntity } from "@calcom/ui/components/unpublished-entity";
import { EventTypeDescriptionLazy as EventTypeDescription } from "@calcom/web/modules/event-types/components";
import EmptyPage from "@calcom/web/modules/event-types/components/EmptyPage";
import type { getServerSideProps } from "@server/lib/[user]/getServerSideProps";
import classNames from "classnames";
import type { InferGetServerSidePropsType } from "next";
import Image from "next/image";
import Link from "next/link";
import { Toaster } from "sonner";

export type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
export function UserPage(props: PageProps) {
	const { users, profile, eventTypes, entity } = props;

	const [user] = users; //To be used when we only have a single user, not dynamic group
	useTheme(profile.theme);

	const isBioEmpty = !user.bio || !user.bio.replace("<p><br></p>", "").length;

	const isEmbed = useIsEmbed(props.isEmbed);
	const eventTypeListItemEmbedStyles = useEmbedStyles("eventTypeListItem");
	const shouldAlignCentrallyInEmbed =
		useEmbedNonStylesConfig("align") !== "left";
	const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
	const {
		// So it doesn't display in the Link (and make tests fail)
		user: _user,
		orgSlug: _orgSlug,
		redirect: _redirect,
		...query
	} = useRouterQuery();

	if (entity.considerUnpublished) {
		return (
			<div className="flex h-full min-h-[calc(100dvh)] items-center justify-center">
				<UnpublishedEntity {...entity} />
			</div>
		);
	}

	const isEventListEmpty = eventTypes.length === 0;
	const isOrg = !!user?.profile?.organization;
	const showReachResponseBrand = !isEmbed;

	return (
		<div
			className={classNames(
				shouldAlignCentrally ? "mx-auto" : "",
				isEmbed ? "max-w-3xl" : "",
				showReachResponseBrand &&
					"relative isolate min-h-screen bg-[#f3f6f4] dark:bg-[#111713]",
			)}
		>
			{showReachResponseBrand && (
				<>
					<div
						aria-hidden
						className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
					>
						<div className="absolute -left-24 -top-28 h-80 w-80 rounded-full bg-[#39de77]/15 blur-3xl dark:bg-[#39de77]/8" />
						<div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-[#4a4a4a]/10 blur-3xl dark:bg-[#39de77]/5" />
					</div>
					<header className="relative z-10 mx-auto flex w-full max-w-4xl px-4 pt-8 sm:px-6 sm:pt-10">
						<a
							href="https://reachresponse.com"
							className="inline-flex rounded-xl bg-white/95 px-4 py-2.5 shadow-sm ring-1 ring-[#d7e2dc] transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#39de77]"
							rel="home"
						>
							<Image
								priority
								className="h-9 w-auto sm:h-10"
								src={LOGO}
								width={185}
								height={50}
								alt="Reach Response"
							/>
						</a>
					</header>
				</>
			)}
			<main
				className={classNames(
					shouldAlignCentrally ? "mx-auto" : "",
					isEmbed
						? "border-booker border-booker-width  bg-default rounded-md"
						: "",
					showReachResponseBrand
						? "max-w-4xl px-4 pb-16 pt-8 sm:px-6"
						: "max-w-3xl px-4 py-12",
				)}
			>
				<div
					className={classNames(
						"border-subtle bg-default text-default mb-8 overflow-hidden border",
						showReachResponseBrand
							? "relative rounded-2xl border-[#d7e2dc] bg-white shadow-[0_24px_60px_rgba(17,23,19,0.12)] dark:border-[#314139] dark:bg-[#18221d] dark:shadow-[0_24px_60px_rgba(0,0,0,0.35)]"
							: "rounded-xl",
					)}
				>
					{isOrg && user.profile.organization?.bannerUrl && (
						<OrgBanner
							alt={user.profile.organization.name ?? "Organization banner"}
							imageSrc={user.profile.organization.bannerUrl}
							className="p-1 border border-subtle rounded-xl w-full object-cover"
						/>
					)}
					<div
						className={classNames(
							showReachResponseBrand ? "p-6 sm:p-8" : "p-4",
						)}
					>
						{showReachResponseBrand && (
							<div
								aria-hidden
								className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#39de77] via-[#83f08c] to-[#39de77]"
							/>
						)}
						<div
							className={classNames(
								showReachResponseBrand &&
									"flex flex-col items-start gap-5 sm:flex-row sm:items-center",
							)}
						>
							<UserAvatar
								size="lg"
								user={{
									avatarUrl: user.avatarUrl,
									profile: user.profile,
									name: profile.name,
									username: profile.username,
								}}
								className={classNames(
									isOrg && user.profile.organization?.bannerUrl ? "-mt-14" : "",
									showReachResponseBrand && "ring-4 ring-[#39de77]/15",
								)}
							/>
							<div>
								<h1
									className={classNames(
										"font-cal text-emphasis mb-1",
										showReachResponseBrand
											? "text-3xl tracking-tight"
											: "text-xl",
										!showReachResponseBrand &&
											!(isOrg && user.profile.organization?.bannerUrl) &&
											"mt-4",
									)}
									data-testid="name-title"
								>
									{profile.name}
									{!isOrg && user.verified && (
										<Icon
											name="badge-check"
											className={classNames(
												"mx-1 -mt-1 inline h-6 w-6 text-white dark:text-black",
												showReachResponseBrand
													? "fill-[#39de77]"
													: "fill-blue-500",
											)}
										/>
									)}
									{isOrg && (
										<Icon
											name="badge-check"
											className={classNames(
												"mx-1 -mt-1 inline h-6 w-6 text-white dark:text-black",
												showReachResponseBrand
													? "fill-[#39de77]"
													: "fill-yellow-500",
											)}
										/>
									)}
								</h1>
								{!isBioEmpty && (
									<>
										{/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via safeBio */}
										<div
											className="text-default wrap-break-word mt-2 text-sm [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600"
											dangerouslySetInnerHTML={{ __html: props.safeBio }}
										/>
									</>
								)}
								{showReachResponseBrand && (
									<a
										href="https://reachresponse.com"
										className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#4f5d56] transition hover:text-[#079455] dark:text-[#a9b8b0]"
									>
										<span className="h-2 w-2 rounded-full bg-[#39de77] shadow-[0_0_0_4px_rgba(57,222,119,0.14)]" />
										reachresponse.com
									</a>
								)}
							</div>
						</div>
					</div>
				</div>

				<div
					className={classNames(
						showReachResponseBrand ? "grid gap-4 sm:grid-cols-2" : "rounded-md",
						!showReachResponseBrand &&
							!isEventListEmpty &&
							"border-subtle border",
					)}
					data-testid="event-types"
				>
					{eventTypes.map((type) => (
						<Link
							key={type.id}
							style={{ display: "flex", ...eventTypeListItemEmbedStyles }}
							prefetch={false}
							href={{
								pathname: `/${user.profile.username}/${type.slug}`,
								query,
							}}
							passHref
							onClick={async () => {
								sdkActionManager?.fire("eventTypeSelected", {
									eventType: type,
								});
							}}
							className={classNames(
								"bg-default border-subtle dark:bg-cal-muted dark:hover:bg-subtle hover:bg-cal-muted group relative transition",
								showReachResponseBrand
									? "min-h-36 rounded-2xl border border-[#d7e2dc] bg-white shadow-sm hover:-translate-y-1 hover:border-[#39de77] hover:bg-white hover:shadow-lg dark:border-[#314139] dark:bg-[#18221d] dark:hover:border-[#39de77] dark:hover:bg-[#18221d]"
									: "border-b first:rounded-t-md last:rounded-b-md last:border-b-0",
							)}
							data-testid="event-type-link"
						>
							<Icon
								name="arrow-right"
								className={classNames(
									"text-emphasis absolute right-4 top-4 transition",
									showReachResponseBrand
										? "h-9 w-9 rounded-full bg-[#e9fbed] p-2 text-[#079455] opacity-100 group-hover:translate-x-0.5 dark:bg-[#23432f] dark:text-[#83f08c]"
										: "h-4 w-4 opacity-0 group-hover:opacity-100",
								)}
							/>
							{/* Don't prefetch till the time we drop the amount of javascript in [user][type] page which is impacting score for [user] page */}
							<div
								className={classNames(
									"block w-full p-5",
									showReachResponseBrand && "pr-16",
								)}
							>
								<div className="flex flex-wrap items-center">
									<h2
										className={classNames(
											"text-default pr-2 font-semibold",
											showReachResponseBrand ? "text-base" : "text-sm",
										)}
									>
										{type.title}
									</h2>
								</div>
								<EventTypeDescription
									eventType={type}
									isPublic={true}
									shortenDescription
								/>
							</div>
						</Link>
					))}
				</div>

				{isEventListEmpty && <EmptyPage name={profile.name || "User"} />}
			</main>
			<Toaster position="bottom-right" />
		</div>
	);
}

export default UserPage;
